import { useSelector } from 'react-redux';
import cx from 'classnames';

// Design System
import { Message } from 'jl-design-system/components/message/Message';
import { SecondaryButton, UnderlinedButton } from 'jl-design-system/elements/button/Button';

// Types
import { ClickAndCollectCollectionPointProps } from './ClickAndCollectCollectionPoint.types';

// Config
import { JOHN_LEWIS_OWNER_ID, WAITROSE_OWNER_ID } from '@constants/clickCollectConstants';
import deliveryConstants from '@constants/deliveryConstants';
import { useWindowSize } from '@context/window/WindowSizeContext';
import { getDeliveryOptionsSelector } from '@redux/reducers/delivery/deliverySelector';
import isCollectionPointAvailable from '@utils/collection/isCollectionPointAvailable';
import getClickCollectFormattedTime from '@utils/helpers/time';

// Components
import ClickAndCollectStoreIcon from '@components/click-and-collect-store-icon';
import Container from '@components/container';
import DisplayAddress from '@components/display-address/DisplayAddress';
import DistanceInMiles from '@components/distance-in-miles';
import Heading from '@components/heading';
import SaveAsDefaultForm from '@components/save-as-default-form/SaveAsDefaultForm';

// Styles
import styles from './click-and-collect-collection-point.scss';

const ClickAndCollectCollectionPoint = ({
  as: Tag = 'li',
  autoFocus = false,
  collectionPoint = {},
  disabled = false,
  getCollectionPointDatesApiCallActive = false,
  hideBorder = false,
  isCallActive = false,
  isSelected,
  onChangeShopClick,
  onMoreDetailsClick,
  promoted = false,
  savedCollectionPoints = [],
  selectCollectionPoint = () => {},
  selectingCollectionPointId,
}: ClickAndCollectCollectionPointProps) => {
  const deliveryOptions = useSelector(getDeliveryOptionsSelector);
  const { desktopResolution } = useWindowSize();

  const getCollectionTimePriceInfo = () => {
    const { collectionChargeFormatted, collectionTime } = collectionPoint;

    return (
      <>
        Collect {getClickCollectFormattedTime({ time: collectionTime })} for{' '}
        <strong>{collectionChargeFormatted}</strong>
      </>
    );
  };

  const renderChangeShopButton = () => {
    const isAvailable = isCollectionPointAvailable(collectionPoint, deliveryOptions);

    if (isSelected && isAvailable) {
      return (
        <SecondaryButton
          className={styles.changeButton}
          data-testid="click-collect-change-button"
          disabled={disabled || isCallActive}
          onClick={onChangeShopClick}
        >
          Change shop
        </SecondaryButton>
      );
    }

    return null;
  };

  const renderAction = () => {
    const { distanceInMiles } = collectionPoint;

    if (distanceInMiles && distanceInMiles > 0) {
      return <DistanceInMiles className={styles.storeDistance} distanceInMiles={distanceInMiles} />;
    }

    return renderChangeShopButton();
  };

  const { address, distanceInMiles, id, isAvailable, isSaveable, ownerId, storeName, unavailabilityReason } =
    collectionPoint;

  const isDefault = !!savedCollectionPoints.find((point) => point.id === id && point.isDefault);
  const headingId = `click-collect-store-id-${id}`;

  const isSubmitting = !!(collectionPoint.id === selectingCollectionPointId && getCollectionPointDatesApiCallActive);
  const isDisabled =
    disabled || (getCollectionPointDatesApiCallActive && collectionPoint.id !== selectingCollectionPointId);

  return (
    <Tag
      key={id}
      className={cx(styles.item, {
        [styles.itemSelected]: isSelected,
        [styles.hideBorder]: hideBorder,
        [styles.promoted]: promoted,
      })}
      data-testid={`collection-point-${ownerId}`}
    >
      {promoted && (
        <Container background="grey-15" className={styles.promotedHeader} marginBottom="2" paddingX="2" paddingY="1">
          {collectionPoint.ownerId === WAITROSE_OWNER_ID && <>Your nearest Waitrose & Partners shop</>}

          {collectionPoint.ownerId === JOHN_LEWIS_OWNER_ID && <>Your nearest John Lewis & Partners shop</>}
        </Container>
      )}

      <ClickAndCollectStoreIcon promoted={promoted} store={collectionPoint} />

      <Container className={styles.content} relative>
        <Heading className={styles.header} marginBottom="2" tag="h3" testId="cc-collection-point-header" type="s">
          <span className={styles.storeName} data-testid="click-collect-store-name" id={headingId}>
            {storeName}
          </span>

          {renderAction()}
        </Heading>

        <Container
          flex={{ alignItems: 'start', flexWrap: 'wrap' }}
          paddingTop={desktopResolution ? undefined : '2'}
          testId="cc-collection-point-details"
        >
          <div
            className={cx(styles.info, {
              [styles.storeUnavailable]: !isAvailable,
            })}
            data-testid="cc-collection-point-info"
          >
            <DisplayAddress address={address} inline renderPostcodeLast />

            {isAvailable && <p data-testid="cc-collection-point-time-and-price">{getCollectionTimePriceInfo()}</p>}

            {!isAvailable && !isSelected && unavailabilityReason && (
              <Message dataTestId="click-collect-info-unavailable" title={unavailabilityReason} type="information" />
            )}
          </div>

          <Container className={styles.moreDetailsButtonContainer} marginTop={desktopResolution ? undefined : '1'}>
            <UnderlinedButton
              aria-describedby={headingId}
              autoFocus={autoFocus}
              className={styles.moreDetailsButton}
              data-testid="click-collect-details-button"
              disabled={disabled}
              onClick={onMoreDetailsClick}
            >
              Map and opening times
            </UnderlinedButton>
          </Container>

          {!!distanceInMiles && distanceInMiles > 0 && renderChangeShopButton()}

          {!isSelected && isAvailable && (
            <SecondaryButton
              aria-describedby={headingId}
              className={styles.selectButton}
              data-testid="click-collect-select-button"
              disabled={isDisabled}
              onClick={() => selectCollectionPoint({ collectionPoint })}
              submitting={isSubmitting}
            >
              Select shop
            </SecondaryButton>
          )}
        </Container>

        {!isDefault && isSelected && isSaveable && isAvailable && (
          <SaveAsDefaultForm
            disabled={disabled}
            saveAsDefaultObject={collectionPoint}
            type={deliveryConstants.CLICK_AND_COLLECT}
          />
        )}
      </Container>
    </Tag>
  );
};

export default ClickAndCollectCollectionPoint;
